import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { AddUpdateBusinessBranchComponent } from "src/app/add-update-business-branch/add-update-business-branch.component";
import { AddUpdateLicenseComponent } from "src/app/BusinessLicense/add-update-license/add-update-license.component";
import { Business, BusinessBranch, BusinessLicense, createEmptyLicense, License, LICENSE_ACTION, LicenseAction } from "src/model/business";
import { BusinessService } from "src/services/business.service";
import { AddUpdateBusinessComponent } from "../../app/BusinessLicense/add-update-business/add-update-business.component";
import { SharedService } from "../../app/Shared/shared-service.service";

@Injectable({
  providedIn: 'root'
})
export class BusinessStore {
  businessService = inject(BusinessService);
  sharedService = inject(SharedService);

  businessList = signal<Business[]>([]);
  branchList = signal<BusinessBranch[]>([]);
  licenseList = signal<License[]>([]);
  activeLicenseList = signal<License[]>([]);

  businessMap = computed(() => new Map(this.businessList().map(b => [b.Id, b])));
  branchMap = computed(() => new Map(this.branchList().map(b => [b.Id, b])));
  businessNameMap = computed<Map<number, string>>(() => {
    const b = this.businessList();
    return b ? new Map(b.map(c => [c.Id, c.Name])) : undefined;
  });
  branchNameMap = computed<Map<number, string>>(() => {
    const b = this.branchList();
    return b ? new Map(b.map(c => [c.Id, c.Name])) : new Map();
  });
  businessLicenseList = computed<BusinessLicense[]>(() => {
    const list = this.licenseList();
    return list.map(l => ({
      ...l,
      BusinessName: this.businessNameMap()?.get(l.BusinessId),
      BranchName: this.branchNameMap()?.get(l.BranchId)
    })).sort((a, b) => a.BusinessName.toLowerCase().localeCompare(b.BusinessName.toLowerCase()))
  });
  activeLicenseByBranch = computed<Map<number, License[]>>(() => this.sharedService.groupByNumKeyAsMap(this.activeLicenseList(), 'BranchId'));
  branchBusinessIdMap = computed<Map<number, number>>(() => new Map(this.branchList().map(b => [b.Id!, b.BusinessId])));
  branchBusinessMap = computed(() => {
    const relation = this.branchBusinessIdMap();
    const businessMap = this.businessMap();
    const result = new Map<number, Business>()
    // Map.forEach((value, key) => ...)
    relation.forEach((businessId, branchId) => {
      const b = businessMap.get(businessId);
      if (b) result.set(branchId, b);
    });
    return result;
  })
  licenseBusinessIdMap = computed<Map<number, number>>(() => {
    const map = this.branchBusinessIdMap();
    return new Map(this.licenseList().map(l => [l.Id!, map.get(l.BranchId)]));
  });

  constructor() {
    console.log('BusinessStore created');
    this.refresh();

    effect(() => {
      const l = this.licenseList();
      if (l?.length > 0)
        this.getAllActiveLicenses();
    })
  }

  refresh() {
    this.getAllBusinesses();
    this.getAllBusinessBranches();
    this.getAllLicenses();
  }

  async getAllBusinesses() {
    const list = await this.businessService.getAllBusinesses();
    this.businessList.set(list.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase())));
  }

  async getAllBusinessBranches() {
    const b = await this.businessService.getAllBusinessBranches();
    this.branchList.set(b);
  }

  async getAllLicenses() {
    const l = await this.businessService.getAllLicenses();
    this.licenseList.set(l.sort((a, b) => a.ValidUntil.getTime() - b.ValidUntil.getTime()));
  }

  async getAllActiveLicenses() {
    const l = await this.businessService.getAllLicenses({ active: true, expired: false, invalidated: false });
    this.activeLicenseList.set(l.sort((a, b) => a.ValidUntil.getTime() - b.ValidUntil.getTime()));
  }

  addBusiness() {
    const modalRef = this.sharedService.openModal(AddUpdateBusinessComponent, 'regularModal');
    modalRef.componentInstance.passEntry.subscribe((business: Business) => {
      this.businessList.update(list => [...list, business]);
    });
  }

  updateBusiness(business: Business) {
    const modalRef = this.sharedService.openModal(AddUpdateBusinessComponent, 'regularModal');
    modalRef.componentInstance.businessToUpdate = business;
    modalRef.componentInstance.isEdit = true;
    modalRef.componentInstance.passEntry.subscribe((updatedBusiness: Business) => {
      this.businessList.update(list => {
        const i = list.findIndex(b => b.Id === business.Id)
        list.splice(i, 1, updatedBusiness);
        return [...list];
      })
    });
  }

  addBusinessBranch(businessId?: number) {
    const modalRef = this.sharedService.openModal(AddUpdateBusinessBranchComponent, 'regularModal');
    const i = modalRef.componentInstance;
    i.businessList = this.businessList();
    if (businessId !== undefined) {
      i.businessId = businessId;
      i.lockBusiness = true;
    }
    i.passEntry.subscribe((branch: BusinessBranch) => {
      this.branchList.update(list => [...list, branch]);
    });
  }

  updateBusinessBranch(branch: BusinessBranch) {
    const modalRef = this.sharedService.openModal(AddUpdateBusinessBranchComponent, 'regularModal');
    const i = modalRef.componentInstance as AddUpdateBusinessBranchComponent;
    i.businessList = this.businessList();
    i.businessId = branch.BusinessId;
    i.branchToUpdate.set(branch);
    i.isEdit.set(true);
    i.lockBusiness = true;
    i.passEntry.subscribe((b) => {
      this.branchList.update(list => {
        const index = list.findIndex(br => br.Id === b.Id);
        list.splice(index, 1, b);
        return [...list];
      })
    });
  }

  openAddLicense(action: LicenseAction, branch: BusinessBranch, license?: License) {
    const modalRef = this.sharedService.openModal(AddUpdateLicenseComponent, 'regularModal');
    const i = modalRef.componentInstance as AddUpdateLicenseComponent;
    i.businessId = branch.BusinessId;
    i.action.set(action);
    i.businessBranch.set(branch);
    i.lockBusiness = true;
    i.businessList = this.businessList().filter(b => b.Id === branch.BusinessId);
    if (license)
      i.licenseToUpdate = license;
    i.passEntry.subscribe((l: License) => {
      this.licenseList.update(list => {
        l.BusinessId ??= branch.BusinessId;
        list.push(l);
        return [...list];
      })
    });
  }

  addLicense(branch: BusinessBranch) {
    this.openAddLicense(LICENSE_ACTION.Add, branch);
  }

  renewLicense(license: License) {
    const branch = this.branchList().find(b => b.Id == license.BranchId);
    const newDate = new Date(license.ValidUntil);
    newDate.setDate(newDate.getDate() + 1);
    const l = {
      ...createEmptyLicense(),
      Address: { ...branch.Address },
      ContactNo: branch.ContactNo,
      Email: branch.Email,
      ValidSince: newDate
    };
    this.openAddLicense(LICENSE_ACTION.Renew, branch, l);
  }

  updateLicense(license: License) {
    const modalRef = this.sharedService.openModal(AddUpdateLicenseComponent, 'regularModal');
    const i = modalRef.componentInstance as AddUpdateLicenseComponent;
    i.businessId = license.BusinessId;
    i.action.set(LICENSE_ACTION.Update);
    i.businessBranch.set(this.branchList().find(b => b.Id == license.BranchId));
    i.lockBusiness = true;
    i.businessList = this.businessList();
    i.licenseToUpdate = license;
    i.isEdit.set(true);
    i.passEntry.subscribe((updatedLicense: License) => {
      this.licenseList.update(list => {
        const i = list.findIndex(l => l.Id === license.Id);
        updatedLicense.BusinessId ??= list[i].BusinessId;
        list.splice(i, 1, updatedLicense);
        return [...list];
      });
    });
  }
}